import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import Figure from "./Figure";
import { roleBars, timelineMonths } from "./figures";
import type { RoleBar } from "./figures";

/**
 * ============================================================================
 * The career timeline — ONE COMPONENT, TWO SURFACES (D-102).
 * ============================================================================
 *
 * `/dashboard` and the Home strip both drew this timeline, and they drew it
 * DIFFERENTLY. The page showed the earlier-title pill and the role summary; the
 * strip showed the location and the "Current" state. Neither was wrong and
 * neither was complete, and a reader who saw both saw two accounts of one
 * record — which is the owner's instruction here ("make it identical") and also
 * the reason it kept happening: two copies of a layout diverge on the next edit
 * that touches only one of them.
 *
 * So this is the layout, once. What each surface still chooses is WHERE THE CARD
 * GOES, and that is a real difference rather than a leftover:
 *
 *   · On Home the card opens `/dashboard` — the page the strip is a teaser for,
 *     and the destination the owner named.
 *   · On `/dashboard` it opens `/#experience`, which is what that panel's own
 *     blurb promises: "Every card opens the full account in Experience". A card
 *     there linking to the page it is already on would be a link to nowhere.
 *
 * `to` IS REQUIRED. A surface that could omit the destination is a surface that
 * silently stops being clickable, and TypeScript refusing to compile is a better
 * gate than a sweep noticing later — the same call `DashboardTeaser` made for
 * its count cards in D-097.
 *
 * ----------------------------------------------------------------------------
 * EVERY NUMBER GOES THROUGH `<Figure>`
 * ----------------------------------------------------------------------------
 * The span and the tenure are registered ids (`role:<id>:span`, `:tenure`),
 * which is what lets the sweep join them to a source and what makes D-095's
 * rule checkable: the same id on two documents must print the same value. That
 * rule is now trivially satisfied for these eight, because both documents
 * render them through this one component.
 *
 * The BAR is `aria-hidden` decoration and it can be, because every value it
 * draws is stated in text beside it. D-100 is the record of why that matters:
 * this panel used to justify an unlabelled bar by pointing at the Experience
 * section above it, which is an arrangement that breaks silently the day the
 * panel is moved or linked to.
 */
export default function RoleTimeline({ to }: { to: string }): JSX.Element {
	const reduceMotion = useReducedMotion();

	return (
		<ol className="space-y-5">
			{roleBars.map((bar: RoleBar, index: number) => (
				<motion.li
					key={bar.id}
					className="card-shell"
					initial={reduceMotion ? false : { opacity: 0, y: 24 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.2 }}
					transition={{
						duration: 0.5,
						delay: Math.min(index * 0.06, 0.24),
						ease: [0.22, 1, 0.36, 1]
					}}
				>
					{/* THE WHOLE CARD IS ONE LINK. A role card carries no other
					    interactive content, so a wrapping `<Link>` is one tab stop and
					    one entry in the accessibility tree — the stretched `::after` is
					    for cards that carry links of their own (D-099), and reaching
					    for it here would add machinery to solve a problem this card
					    does not have. */}
					<Link
						to={to}
						aria-label={`${bar.title} at ${bar.company} — open the full record`}
						className="card-face block p-5 no-underline focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
					>
						{/* The brand-ramp hairline every card section on this site
						    carries since D-099. Decorative, hence the brand ramp
						    rather than a `--cat-*` hue: a category hue is INFORMATION
						    here and a role has no category. */}
						<span
							aria-hidden="true"
							className="mb-4 block h-[3px] w-10 rounded-full bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]"
						/>

						<div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
							<h3 className="text-base font-semibold leading-snug text-[var(--text-strong)]">
								{bar.title}
							</h3>
							<p className="shrink-0 text-xs tabular-nums text-[var(--text-muted)]">
								<Figure id={bar.spanFigureId} />
								<span aria-hidden="true"> · </span>
								<Figure
									id={bar.tenureFigureId}
									className="font-semibold text-[var(--text-secondary)]"
								/>
							</p>
						</div>

						<p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[var(--text-secondary)]">
							<span className="font-medium">{bar.company}</span>
							{bar.location && (
								<>
									<span aria-hidden="true" className="text-[var(--text-muted)]">
										·
									</span>
									<span className="text-[var(--text-muted)]">{bar.location}</span>
								</>
							)}
							{/* The same word and the same hue as the pill on the
							    Experience card (D-100): the open role says so, rather
							    than leaving a reader to infer it from a date range or
							    from the colour of a bar. */}
							{bar.current && (
								<span className="pill-tint border-[var(--cat-emerald-border)] bg-[var(--cat-emerald-fill)] text-[var(--cat-emerald-text)]">
									Current
								</span>
							)}
						</p>

						{/* NO PROGRESSION PILL. It read "Product Architect → Product
						    Manager" here and on the Home strip — one component, two
						    surfaces (D-102) — and the owner ruled on 2026-09-06 that
						    the UI shows the departing title alone. `formerTitle` is
						    STILL IN `experience.json` and is deliberately read by
						    nothing: it is the guard that keeps a resume sync from
						    writing "Product Architect" into `title`, which is exactly
						    what D-013 did. `role.mjs` asserts that the field is
						    present in the layer and absent from every built document,
						    so this cannot come back unnoticed and the field cannot be
						    deleted as dead (D-093's retirement assertion). */}

						<span
							aria-hidden="true"
							className="mt-4 block h-2.5 w-full rounded-full bg-[var(--border-soft)]"
						>
							<span
								className="block h-full rounded-full"
								style={{
									marginLeft: `${(bar.offsetMonths / timelineMonths) * 100}%`,
									width: `${Math.max(4, (bar.months / timelineMonths) * 100)}%`,
									backgroundColor: bar.current ? "var(--cat-emerald-text)" : "var(--cat-sky-text)"
								}}
							/>
						</span>

						<p className="mt-4 text-sm leading-relaxed text-[var(--text-secondary)]">{bar.summary}</p>
					</Link>
				</motion.li>
			))}
		</ol>
	);
}
